import { telemetry } from "telemetry";

import type { Captured } from "./offlineHostData";

/** A scene-open request's answer: the server's, or the capture's while the viewer is offline. */
export type SceneOpenAnswer<T, C> =
    | { readonly from: "network"; readonly data: T }
    | {
          readonly from: "capture";
          readonly captured: C;
          /** Asks the server, for when the capture can't be kept. */
          readonly network: () => Promise<T>;
      };

/**
 * A scene-open request (scene config, project, model tree permissions, permission check, the
 * coordinate system), answered from the scene's capture while the viewer is offline.
 *
 * Online, the server's answer stands, however long it takes, errors included. Offline, the capture
 * answers, or the network when there is nothing captured. Captured data is only used offline,
 * where the edits that could overwrite the server's changes since the download are disabled.
 */
export async function requestSceneOpen<T, C>({
    isOnline,
    network,
    capture,
}: {
    isOnline: boolean;
    network: () => Promise<T>;
    /** What the capture answers, or `undefined` for nothing. One that rejects is taken for nothing. */
    capture: (() => Promise<C | undefined>) | undefined;
}): Promise<SceneOpenAnswer<T, C>> {
    if (isOnline || !capture) {
        return { from: "network", data: await network() };
    }
    const captured = await capture().catch(reportCaptureFailure);
    return captured !== undefined ? { from: "capture", captured, network } : { from: "network", data: await network() };
}

/** Reports a capture that failed to read, which then reads as none. */
function reportCaptureFailure(error: unknown): undefined {
    telemetry.trackCaughtException({
        source: "offline.sceneOpen.capture",
        category: "data",
        error,
    });
    return undefined;
}

/**
 * The answer, if it is the server's or the capture is of the version the engine loaded. The
 * server's otherwise, waited for: a capture is read only with its own version loaded, since
 * object ids belong to one version (the strict rule).
 */
export async function keepIfVersion<T>(
    answer: SceneOpenAnswer<T, Captured<T>>,
    loadedManifestId: string | undefined,
): Promise<T> {
    if (answer.from === "network") {
        return answer.data;
    }
    return loadedManifestId !== undefined && answer.captured.manifestId === loadedManifestId
        ? answer.captured.data
        : await answer.network();
}

/** The capture's blob, if it is of the version the engine loaded. */
export function ofVersion<T>(captured: Captured<T> | undefined, loadedManifestId: string | undefined): T | undefined {
    return loadedManifestId !== undefined && captured?.manifestId === loadedManifestId ? captured.data : undefined;
}
